/**
 * De arena: je Krabbelgotchi tegen die van een collega, allebei tegelijk online.
 *
 * Je daagt iemand uit die op dat moment aan de paal zit. Hij krijgt het meteen te
 * zien, neemt aan, en dan kiezen jullie elke ronde allebei een aanval. Zodra beide
 * keuzes binnen zijn gaat de ronde los, bij jullie allebei precies hetzelfde.
 *
 * Op de server staat niet de stand maar het **zaadje en de rij zetten**. Allebei de
 * browsers rekenen daar hetzelfde gevecht uit. Zou de stand er staan, dan zou de
 * laatste schrijver winnen in plaats van de beste speler, en dan is een uitslag
 * niets waard. Zie speelNa in lib/battle.js.
 *
 * Eén schrijver per ronde: de uitdager legt de ronde vast zodra beide keuzes er
 * zijn. Twee schrijvers op dezelfde regel zouden elkaars ronde overschrijven.
 */

import { api, asJson } from '../lib/api.js';
import { ONLINE_MS, PB_URL } from '../lib/config.js';
import { playMemPaar, playWormBonus, playWormDood, playWormHap } from '../lib/sound.js';
import * as vecht from '../lib/battle.js';
import * as tam from '../lib/tamagotchi.js';

const COL = 'paal_arena';

/** Zo lang mag een uitdaging open blijven staan voor hij vervalt. */
const WACHT_MS = 90e3;

export default {
  openArena() {
    if (!this.state.tamBeest) return;
    this.setState({ tamMode: 'arena', arenaFout: '' }, () => {
      this.tamHaalBuren();
      this.haalArena();
    });
  },

  sluitArena() { return this.setState({ tamMode: '' }); },

  /** Ben ik de uitdager in dit potje? */
  benIkUitdager(rij) { return !!rij && rij.uitdagerId === this.state.accountId; },

  /** Het beestje van de tegenstander, uit de burenlijst. */
  tegenstanderVan(rij) {
    const ik = this.state.accountId;
    const wie = this.benIkUitdager(rij) ? rij.verdedigerId : rij.uitdagerId;
    const r = (this.state.tamBuren || []).find((b) => b.authorId === wie);
    if (!r) return null;
    return {
      geboren: Number(r.geboren) || 0, gevoed: Number(r.gevoed) || 0,
      gespeeld: Number(r.gespeeld) || 0, geschept: Number(r.geschept) || 0,
      naam: tam.naamOk(r.naam), soort: tam.soortVanBeest(r), authorId: wie, ik
    };
  },

  /**
   * De potjes ophalen die met mij te maken hebben.
   *
   * Loopt er een, dan wordt dat het gevecht op het scherm. Dit is ook de plek waar
   * de live-lijn binnenkomt: er verandert iets aan een regel, wij halen op, en het
   * scherm rekent de nieuwe stand uit de zetten.
   */
  async haalArena() {
    const ik = this.state.accountId;
    if (!PB_URL || !ik) return;
    try {
      const q = encodeURIComponent(`uitdagerId="${ik}" || verdedigerId="${ik}"`);
      const res = await api(`${PB_URL}/api/collections/${COL}/records?perPage=100&sort=-created&filter=${q}`);
      if (!res.ok) { this.setState({ arenaMist: true, arenaStatus: res.status }); return; }
      const rijen = (await res.json()).items || [];
      const nu = Date.now();
      // Het potje dat nu speelt: open of bezig, en niet verlopen.
      const loopt = rijen.find((r) => (r.status === 'open' || r.status === 'bezig')
        && nu - Date.parse(r.updated || r.created || '') < WACHT_MS * 4);
      const klaarNu = rijen.find((r) => r.status === 'klaar'
        && this.arenaVorige && r.id === this.arenaVorige.id && r.id !== this.arenaWeg);
      const vorige = this.arenaVorige;
      this.arenaVorige = loopt || klaarNu || null;
      this.setState({ arenaLijst: rijen, arenaRij: loopt || klaarNu || null, arenaMist: false, arenaStatus: 0 },
        () => {
          /**
           * Laten horen dat er iets gebeurd is.
           *
           * Je zit te wachten op de ander, dus je kijkt misschien niet. Een ronde die
           * rond is klinkt als een tik; het einde klinkt als winst of verlies. Alleen
           * bij een echte verandering, anders piept elke ophaalronde.
           */
          const nuZetten = vecht.aantalRondes(this.state.arenaRij && this.state.arenaRij.zetten);
          const toenZetten = vecht.aantalRondes(vorige && vorige.zetten);
          if (klaarNu && (!vorige || vorige.status !== 'klaar')) {
            const g = this.arenaGevecht(klaarNu);
            this.spelGeluid(g && g.gewonnen === 'mij' ? playMemPaar : playWormDood);
          } else if (nuZetten > toenZetten) {
            this.spelGeluid(playWormHap);
          }
          this.arenaRondeAf();
          this.tamTeken();
        });
    } catch (e) {
      this.setState({ arenaMist: true, arenaStatus: 0 });
    }
  },

  /** Iemand uitdagen die nu online is. */
  async daagUit(authorId) {
    const ik = this.state.accountId;
    if (!PB_URL || !ik || !authorId || this.state.arenaBezig) return;
    this.setState({ arenaBezig: true, arenaFout: '' });
    try {
      const zaad = (Date.now() % 2147483647) >>> 0;
      const res = await api(`${PB_URL}/api/collections/${COL}/records`, asJson('POST', {
        uitdagerId: ik, verdedigerId: authorId, zaad, zetten: '',
        keuzeUit: '', keuzeVer: '', status: 'open', gewonnen: false, beurten: 0
      }));
      if (!res.ok) throw new Error(String(res.status));
      this.setState({ arenaBezig: false }, this.haalArena);
      this.spelGeluid(playWormBonus);
    } catch (e) {
      this.setState({ arenaBezig: false, arenaFout: this.arenaFoutTekst(e) });
    }
  },

  arenaFoutTekst(e) {
    return e.message === '404'
      ? 'De collectie paal_arena bestaat nog niet op de server.'
      : 'Het lukte niet (' + e.message + ').';
  },

  /** De uitdaging aannemen. */
  async neemAan() {
    const rij = this.state.arenaRij;
    if (!rij || this.benIkUitdager(rij)) return;
    await this.arenaSchrijf(rij.id, { status: 'bezig' });
    this.spelGeluid(playWormBonus);
  },

  /** Een uitdaging afslaan, of er zelf mee stoppen. */
  async stopGevecht() {
    const rij = this.state.arenaRij;
    if (!rij) return;
    await this.arenaSchrijf(rij.id, { status: 'af' });
    this.setState({ arenaRij: null });
  },

  async arenaSchrijf(id, data) {
    if (!PB_URL || !id) return;
    try {
      const res = await api(`${PB_URL}/api/collections/${COL}/records/${id}`, asJson('PATCH', data));
      if (!res.ok) throw new Error(String(res.status));
      this.haalArena();
    } catch (e) {
      this.setState({ arenaFout: this.arenaFoutTekst(e) });
    }
  },

  /**
   * Jouw keuze voor deze ronde.
   *
   * Je zet hem neer en wacht op de ander. Zodra allebei de keuzes er staan legt de
   * uitdager de ronde vast; zie arenaRondeAf.
   */
  kiesAanval(id) {
    const rij = this.state.arenaRij;
    if (!rij || rij.status !== 'bezig') return;
    const mijn = this.benIkUitdager(rij) ? 'keuzeUit' : 'keuzeVer';
    if (rij[mijn]) return;
    this.arenaSchrijf(rij.id, { [mijn]: id });
  },

  /**
   * Staan beide keuzes er? Dan is de ronde rond.
   *
   * Alleen de uitdager schrijft hem weg. Zouden ze het allebei doen, dan komt de
   * tweede over de eerste heen en verdwijnt er een ronde.
   */
  arenaRondeAf() {
    const rij = this.state.arenaRij;
    if (!rij || rij.status !== 'bezig' || !this.benIkUitdager(rij)) return;
    if (!rij.keuzeUit || !rij.keuzeVer) return;
    const g = this.arenaGevecht(rij);
    if (!g) return;
    const na = vecht.doeBeurt(g, rij.keuzeUit, rij.keuzeVer);
    const zetten = vecht.metZet(rij.zetten, rij.keuzeUit, rij.keuzeVer);
    const patch = { zetten, keuzeUit: '', keuzeVer: '' };
    if (na.klaar) {
      patch.status = 'klaar';
      patch.beurten = na.beurt;
      // `gewonnen` gaat over de uitdager, en die ben ik hier.
      patch.gewonnen = na.gewonnen === 'mij';
    }
    this.arenaSchrijf(rij.id, patch);
  },

  /**
   * Het gevecht zoals het er nu voor staat, uitgerekend uit de zetten.
   *
   * Altijd vanuit jouw kant gezien: `mij` is jouw beestje, ook als jij de
   * verdediger bent. Daarom worden de zetten omgedraaid als je niet de uitdager
   * bent; de rij op de server staat altijd op volgorde uitdager-verdediger.
   */
  arenaGevecht(rij) {
    const r = rij || this.state.arenaRij;
    const mij = this.state.tamBeest;
    const hem = r && this.tegenstanderVan(r);
    if (!r || !mij || !hem) return null;
    const zetten = this.benIkUitdager(r)
      ? r.zetten
      : String(r.zetten || '').split(',').filter(Boolean)
        .map((z) => z.split('>').reverse().join('>')).join(',');
    // De uitdager gaat voor bij gelijke conditie, ook als jij de verdediger bent.
    // Anders zet elke browser zichzelf vooraan; zie nieuwGevecht in lib/battle.js.
    return vecht.speelNa(mij, hem, Date.now(), Number(r.zaad) || 1, zetten, this.benIkUitdager(r));
  },

  arenaVals() {
    const ik = this.state.accountId || '';
    const rij = this.state.arenaRij;
    const g = this.arenaGevecht();
    const hem = rij && this.tegenstanderVan(rij);
    const uitdager = this.benIkUitdager(rij);
    const mijnKeuze = rij ? (uitdager ? rij.keuzeUit : rij.keuzeVer) : '';
    const lijst = (this.state.arenaLijst || []).filter((r) => r.status === 'klaar');
    const gewonnen = lijst.filter((r) => (r.uitdagerId === ik && r.gewonnen) || (r.verdedigerId === ik && !r.gewonnen)).length;
    const naamVan = (id) => {
      const a = (this.state.accounts || []).find((x) => x.id === id);
      return a ? a.name : 'iemand';
    };
    const nu = Date.now();
    const online = (id) => {
      const a = (this.state.accounts || []).find((x) => x.id === id);
      const t = a && Date.parse(a.lastSeen || '');
      return !!t && nu - t < ONLINE_MS;
    };

    return {
      showArena: this.state.tamMode === 'arena',
      openArena: this.openArena,
      sluitArena: this.sluitArena,
      arenaKopStyle: { fontSize: '13px', fontWeight: 700, textAlign: 'center' },
      arenaKop: !rij ? 'Wie daag je uit?'
        : rij.status === 'open'
          ? (uitdager ? 'Wachten op ' + naamVan(rij.verdedigerId) + '…' : naamVan(rij.uitdagerId) + ' daagt je uit')
          : 'Tegen ' + (hem ? tam.noem(hem) : 'de ander'),

      // ---- de kiezer: alleen wie nu online is ----
      showArenaKiezer: !rij,
      arenaKandidaten: (this.state.tamBuren || []).filter((r) => online(r.authorId)).map((r) => {
        const beestje = {
          geboren: Number(r.geboren) || 0, gevoed: Number(r.gevoed) || 0,
          gespeeld: Number(r.gespeeld) || 0, geschept: Number(r.geschept) || 0
        };
        const st = tam.standVan(beestje, nu, 12);
        return {
          naam: tam.naamOk(r.naam) || 'Naamloos',
          van: 'van ' + naamVan(r.authorId),
          niveau: 'niveau ' + vecht.krachtenVan(beestje, nu).niveau,
          uit: st.fase === 'ei' || !!this.state.arenaBezig,
          titel: st.fase === 'ei' ? 'Dat is nog een ei' : 'Daag hem uit',
          onClick: () => this.daagUit(r.authorId),
          rijStyle: {
            display: 'flex', alignItems: 'center', gap: '9px', width: '100%', textAlign: 'left',
            boxSizing: 'border-box', padding: '7px 10px', borderRadius: '12px', fontSize: '13px',
            border: '3px solid var(--ink)', background: 'var(--lila)'
          },
          naamStyle: { fontWeight: 700, flex: '1 1 auto', minWidth: '0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
          vanStyle: { fontSize: '11px', fontWeight: 600, color: 'var(--tekst-2)', flex: '0 0 auto' },
          niveauStyle: { fontSize: '11px', fontWeight: 700, color: 'var(--paars)', flex: '0 0 auto' }
        };
      }),
      showArenaLeeg: !rij && !this.state.arenaMist
        && !(this.state.tamBuren || []).filter((r) => online(r.authorId)).length,
      // Dit is geen uitleg maar de reden dat de lijst leeg is: vechten kan alleen
      // met iemand die er op dat moment ook is.
      arenaLeeg: 'Er is nu niemand anders online om tegen te vechten.',

      /**
       * De arena staat nog niet op de server.
       *
       * Zonder deze regel zag je "niemand online" en dacht je dat het aan je
       * collega's lag, terwijl er een collectie ontbreekt. Dat kost een half uur
       * zoeken op de verkeerde plek.
       */
      showArenaMist: !!this.state.arenaMist && !!ik,
      arenaMistTekst: this.state.arenaStatus === 403
        ? 'De arena staat op slot: zet List, View, Create en Update open op paal_arena.'
        : 'De collectie paal_arena bestaat nog niet op de server.',

      // ---- een uitdaging die klaarstaat ----
      showArenaVraag: !!rij && rij.status === 'open' && !uitdager,
      showArenaWachten: !!rij && rij.status === 'open' && uitdager,
      neemAan: this.neemAan,
      stopGevecht: this.stopGevecht,

      // ---- het gevecht ----
      showArenaVecht: !!(rij && rij.status === 'bezig' && g && !g.klaar),
      arenaWachtOpAnder: !!mijnKeuze,
      arenaAanvallen: vecht.AANVALLEN.map((a) => ({
        naam: a.naam,
        titel: a.kracht
          ? a.naam + ': ' + (a.kans === 1 ? 'raakt altijd' : Math.round(a.kans * 100) + '% raak')
          : 'Hij slaat daarna zachter',
        uit: !!mijnKeuze,
        onClick: () => this.kiesAanval(a.id),
        style: {
          width: '100%', height: '38px', justifyContent: 'center', borderRadius: '12px',
          fontSize: '13px', minWidth: '0',
          background: mijnKeuze === a.id ? 'var(--paars)' : '#fff',
          color: mijnKeuze === a.id ? '#fff' : 'var(--ink)'
        }
      })),
      arenaAanvalRasterStyle: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: '7px', minWidth: '0' },
      arenaLog: mijnKeuze ? 'Wachten op de ander…' : (g && g.log.length ? g.log.slice(-2).join(' ') : ''),
      arenaLogStyle: { fontSize: '11px', fontWeight: 600, color: 'var(--tekst-2)', textAlign: 'center', lineHeight: 1.4, minHeight: '30px' },

      // ---- afgelopen ----
      showArenaKlaar: !!(rij && rij.status === 'klaar' && g),
      arenaUitslag: g && g.klaar
        ? (g.gewonnen === 'mij' ? 'Gewonnen!' : g.gewonnen === 'gelijk' ? 'Gelijkspel.' : 'Verloren.')
        : '',
      arenaUitslagStyle: {
        fontSize: '15px', fontWeight: 700, textAlign: 'center',
        color: g && g.gewonnen === 'mij' ? 'var(--groen-ink)' : 'var(--ink)'
      },
      /**
       * Het afgelopen potje wegleggen.
       *
       * Het id onthouden, anders vist haalArena hem er een seconde later zo weer bij:
       * hij staat op klaar en hoort bij mij, dus hij komt gewoon terug in beeld en
       * dan lijkt de knop stuk.
       */
      arenaOpnieuw: () => {
        this.arenaWeg = rij ? rij.id : null;
        this.arenaVorige = null;
        this.setState({ arenaRij: null });
      },

      arenaStand: lijst.length ? gewonnen + ' gewonnen, ' + (lijst.length - gewonnen) + ' verloren' : 'Nog geen potjes gespeeld.',
      arenaStandStyle: { fontSize: '11px', fontWeight: 600, color: 'var(--tekst-2)', textAlign: 'center' },
      showArenaFout: !!this.state.arenaFout,
      arenaFout: this.state.arenaFout,
      arenaKnopStyle: { width: '100%', height: '38px', justifyContent: 'center', borderRadius: '12px', fontSize: '13px' },
      arenaLijstStyle: {
        display: 'flex', flexDirection: 'column', gap: '7px',
        maxHeight: '190px', overflowY: 'auto', overscrollBehavior: 'contain'
      }
    };
  }
};
